import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, useApi } from "@/lib/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Users, Upload, Columns3, Inbox, FileText, FolderArchive,
  Sparkles, LogOut, Search, ShieldCheck, Settings as SettingsIcon, BookOpen
} from "lucide-react";
import { initials } from "@/lib/format";
import { useEffect, useState } from "react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  { to: "/leads", label: "Leads & Contacts", icon: Users, tid: "nav-leads" },
  { to: "/pipeline", label: "Pipeline", icon: Columns3, tid: "nav-pipeline" },
  { to: "/leads/import", label: "Import", icon: Upload, tid: "nav-import" },
  { to: "/approvals", label: "Owner Approvals", icon: Inbox, tid: "nav-approvals" },
  { to: "/templates", label: "Outreach Templates", icon: FileText, tid: "nav-templates" },
  { to: "/forms", label: "Forms Library", icon: FolderArchive, tid: "nav-forms" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "nav-settings" },
  { to: "/assistant", label: "AI Assistant", icon: Sparkles, tid: "nav-assistant" },
  { to: "/help", label: "Help & Guide", icon: BookOpen, tid: "nav-help" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { get } = useApi();
  const nav = useNavigate();
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    const fetchStats = async () => {
      try {
        const r = await get("/dashboard/stats");
        if (alive) setPendingApprovals(r.data.pending_approvals);
      } catch (err) {
        // Header is non-critical; log at debug level so real problems remain visible.
        if (process.env.NODE_ENV !== "production") {
          console.debug("[AppLayout] dashboard/stats fetch failed:", err?.message || err);
        }
      }
    };
    fetchStats();
    const t = setInterval(fetchStats, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [get]);

  const onSearch = (e) => {
    if (e.key === "Enter" && search.trim()) {
      nav(`/leads?q=${encodeURIComponent(search.trim())}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top header */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="px-6 h-14 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded bg-slate-900 grid place-items-center">
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <span className="font-display text-base font-extrabold tracking-tight text-slate-900">
              ClaimWerks
            </span>
            <Badge variant="outline" className="ml-1 text-[10px] font-semibold tracking-widest uppercase border-slate-300 text-slate-500">
              Owner Console
            </Badge>
          </div>

          <div className="flex-1 max-w-xl mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="global-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearch}
              placeholder="Search leads, case numbers, addresses…"
              className="pl-9 h-9 bg-slate-50 border-slate-200"
            />
          </div>

          <div className="flex items-center gap-3">
            <NavLink
              to="/approvals"
              data-testid="approval-inbox-btn"
              className="relative flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
            >
              <Inbox className="h-4 w-4" />
              <span className="hidden md:inline">Approvals</span>
              {pendingApprovals > 0 && (
                <span data-testid="pending-approvals-count"
                  className="absolute -top-1 -right-3 bg-amber-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 grid place-items-center">
                  {pendingApprovals}
                </span>
              )}
            </NavLink>

            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-slate-900 text-white text-xs font-bold">
                  {initials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block leading-tight">
                <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
                <div className="text-[11px] text-slate-500">{user?.email}</div>
              </div>
              <Button
                data-testid="logout-btn"
                variant="ghost" size="icon"
                onClick={() => { logout(); nav("/login"); }}
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-60 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-3.5rem)] p-3">
          <nav className="space-y-1">
            {NAV.map(({ to, label, icon: Icon, end, tid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={tid}
                className={({ isActive }) => `cw-nav-item ${isActive ? "active" : ""}`}
              >
                <Icon className="h-4 w-4" /> <span>{label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="mt-8 px-3 text-xs text-slate-400 leading-relaxed">
            <p className="font-semibold uppercase tracking-widest text-slate-500 mb-1">Compliance Mode</p>
            <p>All outbound communications require Owner approval. Templates include state-specific disclosures.</p>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="p-6 md:p-8 max-w-[1600px] mx-auto cw-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
